// §VII · CLOSING (visual fork + course block) — ported from the Claude Design canon
// `R10 - §VII - canon F4.html` (project dd7b95d4, data-canon="R10-§VII-F4"; голден снят с F4,
// cc-205; F3 — история, первичный порт был с F2). F4 поверх F3: прошлое — N равных колонок,
// точка в центре, полки до краёв, монотонная кубика Fritsch–Carlson вместо Catmull-Rom, годы —
// подписи левых границ колонок (pastLabels = N значений), now = правая граница последней;
// Shape II — канон формы u(t) от now к E и веер ×0.72/0.86/1.12/1.24; H = 0 → 1 («another year
// of this?», подпись над линией, когда под ней нет места); тики будущего h − n ≥ 3 (s976.d1).
// Силуэт = среднее тронутых слоёв (s836.d1). Layout =
// styles/r10-vii.css (byte-checked by css-parity-vii.test.js). Canon F2 = reconcile под s416:
// no horizon floor (s416.d1), count/ordinal spelled ≤10 / digit ≥11 (s416.d2).
//
// §VII is the closing page: a silhouette chart with a downward-vs-positive FORK. Two editions
// by axis D (bucket = silhouette[N-1] > mean(silhouette[0..N-2]) ? positive : downward, tie →
// downward, s276.d5): downward prints the full Shape I/II projection fork + "Exit the Loop"
// course; positive drops 13 fields, renders 3 in a different edition + a `bridge`, course
// "Make the Engine Visible". Content is hardcoded copy; per-user only the chart data, stage
// count and bucket change. Projections = engine output (system1.redLine.forward.shapeI/shapeII,
// peak-floor) — already in main; wired at assembly. Detector / engine is not touched here.
//
// §VII is SELF-MANAGED like §II–§VI: it renders its OWN artboard shell (<section class=
// "artboard closing-page">), mounted WITHOUT the generic Artboard wrapper. One board → one
// section. The chart() + sheet inner builders are ported VERBATIM and mounted via
// dangerouslySetInnerHTML, so the DOM is byte-identical to the canon (svg-parity-vii). The
// outer <section> (its data-* + fit class) is the React host. `data-peak-check` is deterministic
// (recomputed here); `data-fit` + the fit classes come from the real-browser measurement, baked
// into the board (see sectionVIIBoards.js) — the same бейк as §VI's pagination.

import { filled } from '../../insights/input.js';

const NUMW = { 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen' };
const ORD = { 2: 'second', 3: 'third', 4: 'fourth', 5: 'fifth', 6: 'sixth', 7: 'seventh', 8: 'eighth', 9: 'ninth', 10: 'tenth', 11: 'eleventh', 12: 'twelfth', 13: 'thirteenth', 14: 'fourteenth', 15: 'fifteenth', 16: 'sixteenth' };

// ---------- геометрия чарта (контракт) ----------
const F_W = 1000, F_H = 332, PAD_X = 40, END_X = F_W - PAD_X, NOW_X = (PAD_X + END_X) / 2;
const Y_TOP = 28, Y_BOT = F_H - 50, Y_MID = (Y_TOP + Y_BOT) / 2, AXIS_Y = Y_BOT + 24;
const yv = (v) => Y_MID - (v / 3) * ((Y_BOT - Y_TOP) / 2);
const clamp = (v) => Math.max(-3, Math.min(3, v));
// defensive — no-op for canon (all means finite & within ±3, shapeI/II finite, year labels carry
// no & or <). They keep off-canon engine output from leaking NaN/Infinity/undefined or injecting
// markup into the SVG. Canon values make each identity → svg-parity-vii stays byte-green.
// (qa-reviewer §VII pass.)
const fin = (v) => (Number.isFinite(v) ? v : 0);
const dom = (v) => (Number.isFinite(v) ? clamp(v) : 0);
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
// F4: монотонная кубика (Fritsch–Carlson) — кривая не выходит за значения соседних узлов.
// Одна функция для прошлого и для Shape II (verbatim monoD канона).
function monoD(p) {
  const n = p.length; if (n < 2) return n ? `M ${p[0].x} ${p[0].y}` : '';
  const dx = [], m = [], t = [];
  for (let i = 0; i < n - 1; i++) { dx[i] = p[i + 1].x - p[i].x; m[i] = (p[i + 1].y - p[i].y) / dx[i]; }
  t[0] = m[0]; t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : 3 * (dx[i - 1] + dx[i]) / ((2 * dx[i] + dx[i - 1]) / m[i - 1] + (dx[i] + 2 * dx[i - 1]) / m[i]);
  let d = `M ${p[0].x.toFixed(2)} ${p[0].y.toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) { const h = dx[i] / 3;
    d += ` C ${(p[i].x + h).toFixed(2)} ${(p[i].y + t[i] * h).toFixed(2)} ${(p[i + 1].x - h).toFixed(2)} ${(p[i + 1].y - t[i + 1] * h).toFixed(2)} ${p[i + 1].x.toFixed(2)} ${p[i + 1].y.toFixed(2)}`; }
  return d;
}
// F4: канон формы Shape II (эталон — Бен, нормированный) и веер. Движок отдаёт одно E
// (forward.shapeII); форма и веер — рендер, декоративный (канон: «движок отдаёт одно E»).
const II_T = [0, .08, .18, .30, .42, .54, .66, .78, .90, 1], II_U = [0, .03, .19, .50, .74, .85, .90, .94, .93, 1], II_FAN = [.72, .86, 1.12, 1.24];
// каденция будущих тиков — шаг ~6 лет + конец горизонта, максимум 4 метки;
// F4 (s976.d1): промежуточный тик ближе 3 лет к концу снимается (при H = 25 ’50 и ’51 наезжали)
function futureOffsets(h) {
  const out = []; for (let n = 6; h - n >= 3; n += 6) out.push(n); out.push(h);
  return out.slice(-4);
}

// ---------- чарт (verbatim) ----------
function chart(v) {
  const N = v.means.length, positive = v.bucket === 'positive';
  if (N < 1) return ''; // defensive: empty means → nothing to draw (canon N ≥ 2)
  // Пустой этап силуэта = null (s836.d3): линию читаем через filled() — форму, не 0, иначе на
  // защитной ветке линия проваливалась в ноль там, где bucket уже говорит «вниз». filled НЕ
  // покидает чарт — v.means остаётся сырым (как cover §I). На корпусе null нет → байт-стабильно.
  const m = filled(v.means);
  const rightX = positive ? END_X : NOW_X;
  // F4: равные колонки, точка в центре, полки до краёв, now = правая граница
  const colW = (rightX - PAD_X) / N;
  const pts = m.map((val, i) => ({ x: PAD_X + (i + .5) * colW, y: yv(dom(val)) })); // defensive: dom() = finite + ±3 clamp
  const knots = [{ x: PAD_X, y: pts[0].y }, ...pts, { x: rightX, y: pts[N - 1].y }];
  const path = monoD(knots), nowY = pts[N - 1].y, nowX = rightX;
  const dense = N >= 10;
  const h = v.horizon, H = h == null ? null : Math.max(1, h); // F4: H = 0 → 1

  let g = [2, 0, -2].map((val) => { const y = yv(val), z = val === 0;
    return `<line x1="${PAD_X - 8}" y1="${y}" x2="${END_X + 8}" y2="${y}" stroke="${z ? 'rgba(143,136,122,.20)' : 'rgba(143,136,122,.10)'}" stroke-width="${z ? .6 : .5}" stroke-dasharray="${z ? '2 6' : '1 4'}"></line>`
      + `<text class="sv-val${z ? ' zero' : ''}" x="${PAD_X - 14}" y="${y + 3}" text-anchor="end">${val > 0 ? '+' + val : val}</text>`; }).join('');

  const area = `${path} L ${rightX.toFixed(2)} ${Y_BOT} L ${PAD_X} ${Y_BOT} Z`;
  let sil = `<path d="${area}" fill="url(#cl-fill)" opacity=".55"></path>`
    + [...Array(N + 1)].map((_, i) => `<line x1="${(PAD_X + i * colW).toFixed(2)}" y1="${Y_BOT - 4}" x2="${(PAD_X + i * colW).toFixed(2)}" y2="${Y_BOT + 4}" stroke="rgba(143,136,122,.4)" stroke-width=".6"></line>`).join('')
    + `<path class="fld" data-field="chart.silhouette_recorded" d="${path}" fill="none" stroke="#D48A3C" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"></path>`
    + pts.map((p) => `<circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="${dense ? 2.3 : 2.6}" fill="#E8E2D4"></circle>`).join('');

  let proj = '';
  if (!positive) {
    const sI = dom(v.shapeI), sII = dom(v.shapeII); // defensive: finite + ±3 clamp
    const iy = yv(sI), iiy = yv(sII);
    const dx = END_X - NOW_X, last = dom(m[N - 1]);
    const curve = (ey, bend) => `M ${NOW_X} ${nowY.toFixed(2)} C ${(NOW_X + dx * .32).toFixed(2)} ${(nowY + (ey - nowY) * bend).toFixed(2)} ${(NOW_X + dx * .7).toFixed(2)} ${(nowY + (ey - nowY) * (1 - bend)).toFixed(2)} ${END_X} ${ey.toFixed(2)}`;
    // SHAPE II (F4) — канон формы: v(t) = now + (E − now)·u(t), монотонная кубика
    const lineII = (Ek) => monoD(II_T.map((t, i) => ({ x: NOW_X + t * dx, y: yv(last + (Ek - last) * II_U[i]) })));
    // веер — та же форма с размахом ×k, зажат только конец (у потолка линии сливаются)
    const fanE = II_FAN.map((k) => clamp(last + (sII - last) * k));
    const mainII = lineII(sII);
    proj += `<g class="fld" data-field="chart.shape_ii_projection" data-decorative="fan:4-lines" data-provisional="shape_ii_fan:decorative">`
      + `<circle cx="${END_X}" cy="${iiy.toFixed(2)}" r="24" fill="url(#cl-moss-halo)"></circle>`
      + fanE.map((Ek, k) => { const inner = k === 1 || k === 2; return `<path d="${lineII(Ek)}" fill="none" stroke="#8FA168" stroke-opacity="${inner ? '.42' : '.24'}" stroke-width="${inner ? 1.5 : 1.1}" stroke-linecap="round"></path>`; }).join('')
      + `<path d="${mainII}" fill="none" stroke="#8FA168" stroke-opacity=".22" stroke-width="7" stroke-linecap="round" filter="url(#cl-glow)"></path>`
      + `<path d="${mainII}" fill="none" stroke="#8FA168" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"></path>`
      + `<circle cx="${END_X}" cy="${iiy.toFixed(2)}" r="6" fill="#8FA168" stroke="#0a0b0d" stroke-width="2"></circle></g>`;
    proj += `<g class="fld" data-field="chart.shape_i_projection">`
      + `<path d="${curve(iy, .12)}" fill="none" stroke="#C8482B" stroke-opacity=".82" stroke-width="1.5" stroke-dasharray="3 3" stroke-linecap="round"></path>`
      + `<circle cx="${END_X}" cy="${iy.toFixed(2)}" r="3.2" fill="#C8482B" fill-opacity=".92"></circle></g>`;
    proj += `<text class="sv-tag i" data-field="chart.shape_i_in_chart_tag" x="${END_X + 10}" y="${iy + 4}">shape I</text>`
      + `<text class="sv-tag ii" data-field="chart.shape_ii_in_chart_tag" x="${END_X + 12}" y="${iiy + 4}">shape II</text>`;
    // аннотация печатается при любой дуге; снимается только при duration null (s416.d1).
    // F4: H = 1 → без числа; под Shape I на 24, если до базы меньше 8 — над линией
    if (H) { const txt = H === 1 ? 'another year of this?' : `another ${H} years of this?`;
      const below = iy + 24 <= Y_BOT - 8;
      // над линией: y от минимума кривой Shape I по ширине текста, не от iy (verbatim канона)
      let ay = iy + 24;
      if (!below) { const tw = txt.length * 7.8 + 8, x0 = END_X - 4 - tw, b = .12, y0 = nowY, y1 = nowY + (iy - nowY) * b, y2 = nowY + (iy - nowY) * (1 - b);
        let top = iy; for (let j = 0; j <= 60; j++) { const t = j / 60, u = 1 - t, x = u * u * u * NOW_X + 3 * u * u * t * (NOW_X + dx * .32) + 3 * u * t * t * (NOW_X + dx * .7) + t * t * t * END_X;
          if (x >= x0) { const y = u * u * u * y0 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * iy; top = Math.min(top, y); } }
        ay = top - 10; }
      proj += `<text class="sv-annot" data-field="chart.shape_i_annotation" data-place="${below ? 'below' : 'above'}" x="${END_X - 4}" y="${ay.toFixed(2)}" text-anchor="end">${txt}</text>`; }
  }

  let now = `<g class="fld" data-field="chart.now_axis">`;
  if (!positive) now += `<line x1="${NOW_X}" y1="${Y_TOP - 6}" x2="${NOW_X}" y2="${Y_BOT + 6}" stroke="#C8482B" stroke-opacity=".55" stroke-width=".9" stroke-dasharray="3 3"></line>`;
  now += `<circle cx="${nowX.toFixed(2)}" cy="${nowY.toFixed(2)}" r="30" fill="url(#cl-now-halo)"></circle>`
    + `<circle cx="${nowX.toFixed(2)}" cy="${nowY.toFixed(2)}" r="9" fill="#C8482B" opacity=".5" filter="url(#cl-glow)"></circle>`
    + `<circle cx="${nowX.toFixed(2)}" cy="${nowY.toFixed(2)}" r="5.2" fill="#C8482B"></circle>`
    + `<circle cx="${nowX.toFixed(2)}" cy="${nowY.toFixed(2)}" r="2.2" fill="#0a0b0d"></circle></g>`;

  let axis = `<line x1="${PAD_X}" y1="${Y_BOT}" x2="${END_X}" y2="${Y_BOT}" stroke="rgba(143,136,122,.28)" stroke-width=".6"></line>`
    + `<line x1="${PAD_X}" y1="${Y_BOT - 4}" x2="${PAD_X}" y2="${Y_BOT + 4}" stroke="rgba(143,136,122,.4)" stroke-width=".6"></line>`
    + `<line x1="${END_X}" y1="${Y_BOT - 4}" x2="${END_X}" y2="${Y_BOT + 4}" stroke="rgba(143,136,122,.4)" stroke-width=".6"></line>`;
  // F4: годы начала этапов — на левых границах колонок (pastLabels = N значений);
  // now — на правой границе последней, из system.current_year, поэтому остаётся и при пустых датах
  axis += `<g class="fld" data-field="chart.year_axis_past">` + (v.pastLabels ? v.pastLabels.map((lab, i) =>
    `<text class="sv-tick${dense ? ' dense' : ''}" x="${(PAD_X + i * colW).toFixed(2)}" y="${AXIS_Y}" text-anchor="${i === 0 ? 'start' : 'middle'}">${esc(lab)}</text>`).join('') : '')
    + `<text class="sv-tick now${dense ? ' dense' : ''}" x="${nowX.toFixed(2)}" y="${AXIS_Y}" text-anchor="middle">now</text></g>`;
  if (!positive) {
    const offs = H ? futureOffsets(H) : null;
    const labs = offs ? offs.map((o) => ({ x: NOW_X + (o / H) * (END_X - NOW_X), t: "'" + String(fin(v.nowYear) + o).slice(-2) }))
      : ["'32", "'38", "'44", "'48"].map((t, k) => ({ x: NOW_X + ((k + 1) / 4) * (END_X - NOW_X), t }));
    axis += `<g class="fld" data-field="chart.year_axis_future"${H ? '' : ' data-flag="duration null → статические метки фолбэка"'}>`
      + labs.map((l) => `<text class="sv-tick${dense ? ' dense' : ''}" x="${l.x.toFixed(2)}" y="${AXIS_Y}" text-anchor="middle">${l.t}</text>`).join('') + `</g>`;
  }

  const hudRight = `<span class="k mute" data-field="chart_header_right">unsmoothed · derived</span>`;
  return `<div class="cl-chart-wrap">
<div class="cl-chart-hud"><span class="k" data-field="chart_header_left">silhouette</span><span class="sep">·</span><span class="v">${N} stages recorded</span><span class="spc"></span>${hudRight}</div>
<svg class="cl-chart-svg" viewBox="0 0 ${F_W} ${F_H}" preserveAspectRatio="none">
<defs>
<linearGradient id="cl-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#D48A3C" stop-opacity=".22"></stop><stop offset="100%" stop-color="#C8482B" stop-opacity=".05"></stop></linearGradient>
<radialGradient id="cl-now-halo" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#C8482B" stop-opacity=".55"></stop><stop offset="60%" stop-color="#C8482B" stop-opacity=".10"></stop><stop offset="100%" stop-color="#C8482B" stop-opacity="0"></stop></radialGradient>
<radialGradient id="cl-moss-halo" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#8FA168" stop-opacity=".55"></stop><stop offset="70%" stop-color="#8FA168" stop-opacity=".10"></stop><stop offset="100%" stop-color="#8FA168" stop-opacity="0"></stop></radialGradient>
<filter id="cl-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur in="SourceGraphic" stdDeviation="2.4"></feGaussianBlur></filter>
</defs>
${g}${sil}${proj}${now}${axis}
</svg></div>`;
}

// ---------- лист · inner (verbatim from sheet(); the outer <section> is the React host) ----------
function sheetInner(v) {
  const N = v.means.length, positive = v.bucket === 'positive';
  // s416.d2: прописью до десяти, цифрой с одиннадцатого — одно правило на обе строки
  const suffix = (n) => { const m = n % 100; return (m >= 11 && m <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'); };
  const count = (n) => n <= 10 ? (NUMW[n] || n) : n;
  const ord = (n) => n <= 10 ? (ORD[n] || n) : n + suffix(n); // defensive: ORD starts at 2 → n<2 fallback

  const eyebrow = positive
    ? `<div class="cl-eyebrow" data-field="eyebrow"><span>the page where the line is still yours</span></div>`
    : `<div class="cl-eyebrow" data-field="eyebrow"><span>the page where the line is still yours</span><span class="sep">·</span><span class="mute">two ways set before you</span></div>`;

  const title = `<h2 class="cl-title">
<span class="line1" data-field="page_title.line_1">${count(N)} stages on the map.</span>
<span class="line2" data-field="page_title.line_2"><em>the ${ord(N + 1)} is starting right now.</em></span></h2>`;

  const epigraph = `<div class="cl-epigraph" data-field="slot_a_epigraph"><p>and here we are.</p></div>`;

  const shapes = positive ? '' : `<div class="cl-shapes">
<div class="cl-shape i">
  <span class="cl-shape-tag">shape I</span>
  <span class="cl-shape-eyb" data-field="shape_i_caption.sub_label">if the line continues</span>
  <span class="cl-shape-label" data-field="shape_i_caption.slot_b">the life keeps happening to you. you keep watching it.</span>
</div>
<div class="cl-shape ii">
  <span class="cl-shape-tag">shape II</span>
  <span class="cl-shape-eyb" data-field="shape_ii_caption.sub_label">if it reverses</span>
  <span class="cl-shape-label" data-field="shape_ii_caption.slot_c">you start authoring the life. it stops happening to you.</span>
</div></div>`;

  const bridgeOrTransition = positive
    ? `<p class="cl-bridge" data-field="bridge">What you&#8217;ve just been reading is the architecture behind your growth &#8212; the patterns that hold it up. <span class="brand">Author Mode</span> goes further into those patterns: it teaches you to recognize them as you live, turning what&#8217;s been intuition into something you can read.</p>`
    : `<p class="cl-transition" data-field="transition_italic">both futures are possible. <em>the choice isn&#8217;t on the chart.</em></p>`;

  const course = positive
    ? `<div class="cl-course-eyebrow bare" data-field="course_block.eyebrow">the course</div>
<h3 class="cl-course-title" data-field="course_block.course_title"><span class="line1">Author Mode:</span><span class="line2"><em>Make the Engine Visible.</em></span></h3>`
    : `<div class="cl-course-eyebrow" data-field="course_block.eyebrow">course</div>
<p class="cl-course-opener" data-field="course_block.slot_d_opener">You&#8217;ve read your life. <em>The next move is yours.</em></p>
<h3 class="cl-course-title" data-field="course_block.course_title"><span class="line1">Author Mode:</span><span class="line2"><em>Exit the Loop.</em></span></h3>
<p class="cl-course-pos" data-field="course_block.slot_e_positioning">for people doing everything right and feeling wrong.</p>`;

  const foot = `<div class="cl-foot">
<div class="cl-meta">
  <p class="cl-meta-voice" data-field="course_block.author_line">by someone who <em>walked&nbsp;out</em> himself.</p>
  <div class="cl-meta-rule" aria-hidden="true"></div>
  <dl class="cl-stats" data-field="course_block.metadata_bar">
    <div><dt>13</dt><dd>videos</dd></div>
    <div><dt>11</dt><dd>exercises</dd></div>
    <div><dt>~20<span class="u">m</span></dt><dd>each</dd></div>
    <div><dt>4+<span class="u">h</span></dt><dd>of material</dd></div>
  </dl>
</div>
<div class="cl-cta">
  <div class="cl-cta-next" data-field="course_block.cta_link">next &#8594;</div>
  <a class="cl-cta-link" href="https://authormode.co">authormode.co</a>
  <div class="cl-cta-tag" data-field="course_block.slot_f_cta_tagline">&#8212; a course in authoring your life.</div>
</div></div>`;

  const roman = v.skipVi ? 'VI' : 'VII';
  return `
<div class="ab-paper"></div><div class="ab-grain"></div>
<header class="ab-runner">
  <span data-field="section_header_left"><span class="runner-roman">${roman}</span>closing</span>
  <span class="runner-meta" data-field="section_header_right">life map · the reading</span>
</header>
<div class="cl-sheet">
${eyebrow}
${title}
${epigraph}
${chart(v)}
${shapes}
${bridgeOrTransition}
${course}
${foot}
</div>
<footer class="ab-foot">
  <span data-field="footer_left"><span class="foot-em">life map</span> <span class="foot-faint">·</span> closing <span class="foot-faint">·</span> <span class="foot-amber">end of reading</span></span>
  <span class="foot-page" data-field="footer_right">${roman.toLowerCase()} · door</span>
</footer>
`;
}

// data-peak-check — deterministic (canon build()): сверка Shape II с формулой F4 на downward.
// F4: E = min(3, max(1.5, pastPeak) + 0.35), pastPeak — пик НАРИСОВАННОГО силуэта.
function peakCheck(v) {
  const means = filled(v.means).map(fin);             // s836.d3: null → форма (filled), не 0; fin ловит остаток
  const peak = means.length ? Math.max(...means) : 0; // defensive: empty means
  const want = Math.min(3, Math.max(1.5, peak) + 0.35);
  const s2 = fin(v.shapeII);                           // defensive: shapeII may be undefined
  return `pastPeak ${peak.toFixed(2)} · E ${want.toFixed(2)} · shapeII ${s2.toFixed(2)} · ${Math.abs(want - s2) < 0.02 ? '✓ по формуле F4' : '✗ расхождение, ожидалось ' + want.toFixed(2)}`;
}

export default function SectionVII({ view }) {
  const v = view;
  const N = v.means.length;
  const hz = v.horizon;
  // fit is baked (not computed): the canon measures gap(last block → footer) in a real browser
  // (canon F2 · s417 — fit contract v1, ladder cl-fit-tight→smaller→min). On this set no board
  // triggers a concession, so fitClasses is '' and data-fit is the measured "запас листа Npx".
  // data-overflow-px is '' on canon (empty blocks export); data-fit-env is '' when the canon
  // fonts are loaded. data-peak-check is deterministic and recomputed here.
  const className = 'artboard closing-page' + (v.fitClasses ? ' ' + v.fitClasses : '');
  return (
    <section
      className={className}
      data-screen-label={v.screen}
      data-run={v.source}
      data-canon="R10-§VII-F4"
      data-bucket={v.bucket}
      data-stage-count={N}
      data-horizon={hz === null ? 'null' : String(hz)}
      {...(v.degr ? { 'data-degradation': v.degr } : {})}
      {...(v.flagAttr ? { 'data-flag': v.flagAttr } : {})}
      {...(v.dataFit != null ? { 'data-fit': v.dataFit } : {})}
      data-overflow-px={v.overflowPx ?? ''}
      data-fit-env={v.fitEnv ?? ''}
      {...(v.bucket !== 'positive' ? { 'data-peak-check': peakCheck(v) } : {})}
      dangerouslySetInnerHTML={{ __html: sheetInner(v) }}
    />
  );
}
